'use client';

import { useState, useEffect, RefObject } from 'react';
import { ChevronUp } from 'lucide-react';

interface ScrollToTopProps {
  scrollableDivRef: RefObject<HTMLDivElement | null>;
}

export default function ScrollToTop({ scrollableDivRef }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  useEffect(() => {
    const scrollContainer = scrollableDivRef.current;

    const toggleVisibility = (): void => {
      if (scrollContainer && scrollContainer.scrollTop > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', toggleVisibility);
    }

    // Cleanup
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', toggleVisibility);
      }
    };
  }, [scrollableDivRef]);

  const scrollToTop = (): void => {
    setIsAnimating(true);

    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      // Reset animation state after the scroll completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <button
      className={`fixed z-50 bottom-6 right-6 sm:bottom-10 sm:right-10 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white shadow-lg transition-all duration-300 ease-out hover:shadow-[var(--primary)]/25 hover:scale-110 focus:outline-none ${
        isAnimating ? 'animate-bounce' : ''
      }`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <div className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-full">
        {/* Animated border */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-[var(--secondary)] to-[var(--primary)] opacity-70 animate-spin-slow rounded-full"
          style={{ animationDuration: '8s' }}
        ></div>
        <div className="absolute inset-[2px] rounded-full bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] flex items-center justify-center">
          <ChevronUp className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
        </div>
      </div>
    </button>
  );
}
